import Glide from '@glidejs/glide';

/**
 * @typedef {object} Gallery
 * @property {string} id
 * @property {HTMLDialogElement} dialogEl
 * @property {HTMLElement} counterEl
 * @property {HTMLElement} captionEl
 * @property {HTMLElement} mainSliderEl
 * @property {Glide | null} mainSlider
 * @property {HTMLElement | undefined} thumbnailsSliderEl
 * @property {Glide | null} thumbnailsSlider
 */

class Gallery {
	/**
	 * @type {Gallery | null}
	 */
	static instance = null;

	/**
	 * @type {Gallery[]}
	 */
	galleries = [];

	/**
	 * @type {Element[]}
	 */
	controls = [];

	_selectors = {
		gallery: '.js-tk-gallery',
		control: '.js-tk-gallery-control',
	};

	constructor() {
		if (!Gallery.instance) {
			this._init();

			Gallery.instance = this;
		}

		return Gallery.instance;
	}

	/**
	 * Reloads all galleries and toggles
	 */
	update() {
		this._init();
	}

	/**
	 * @param {string} id
	 * @returns {Gallery}
	 */
	getGalleryById(id) {
		const gallery = this.galleries.find((item) => item.id === id);

		if (!gallery) {
			throw `Gallery not found: ${id}`;
		}

		return gallery;
	}

	/**
	 * @param {string} id
	 * @param {number?} slideIndex
	 */
	openGallery(id, slideIndex = 0) {
		const gallery = this.getGalleryById(id);

		if (gallery.dialogEl.open) {
			return;
		}

		gallery.dialogEl.showModal();
		window.LazyImage.update();

		if (gallery.mainSlider) {
			gallery.mainSlider.go(`=${slideIndex}`);
		} else {
			this._createGallerySliders(gallery, slideIndex);
		}
	}

	/**
	 * @param {string} id
	 * @private
	 */
	closeGallery(id) {
		const gallery = this.getGalleryById(id);

		if (!gallery.dialogEl.open) {
			return;
		}

		gallery.dialogEl.close();
	}

	/**
	 * @param {string} id
	 * @param {number} slideIndex
	 * @private
	 */
	navigateGallery(id, slideIndex) {
		const gallery = this.getGalleryById(id);
		gallery.mainSlider.go(`=${slideIndex}`);
	}

	/**
	 * Initializes all the galleries and binds the toggles
	 * @private
	 */
	_init() {
		// Create Galleries
		document.querySelectorAll(this._selectors.gallery).forEach((element) => {
			const hasGallery = this.galleries.some((gallery) => gallery.id === element.dataset.tkGalleryId);

			if (!hasGallery) {
				const gallery = this._createGallery(element);
				this.galleries.push(gallery);
			}
		});

		// Bind Controls
		document.querySelectorAll(this._selectors.control).forEach((element) => {
			const hasControl = this.controls.some((toggle) => toggle === element);

			if (!hasControl) {
				element.addEventListener('click', (event) => {
					event.preventDefault();

					const id = element.dataset.tkGalleryId;
					const action = element.dataset.tkGalleryAction || 'open';
					const slideIndex = +element.dataset.tkGallerySlide || 0;

					switch (action) {
						case 'open':
							return this.openGallery(id, slideIndex);
						case 'close':
							return this.closeGallery(id);
						case 'navigate':
							return this.navigateGallery(id, slideIndex);
					}
				});

				this.controls.push(element);
			}
		});
	}

	/**
	 * Creates the whole gallery dialog with sliders
	 * @param {Element} element
	 * @private
	 * @returns {Gallery}
	 */
	_createGallery(element) {
		const galleryId = element.dataset.tkGalleryId;
		const hasThumbnails = element.dataset.tkGalleryThumbnails !== undefined;

		const slidesList = element.children;

		const dialogEl = document.createElement('dialog');
		dialogEl.className = 'c-tk-gallery';
		document.body.appendChild(dialogEl);

		// Header
		const header = document.createElement('header');
		header.className = 'c-tk-gallery__header';
		dialogEl.appendChild(header);

		// Counter
		const counterEl = document.createElement('span');
		counterEl.className = 'c-tk-gallery__header-counter';
		header.appendChild(counterEl);

		// Actions
		const actionsEl = document.createElement('div');
		actionsEl.className = 'c-tk-gallery__header-actions';

		const closeButtonEl = document.createElement('button');
		closeButtonEl.type = 'button';
		closeButtonEl.className = 'c-tk-icon-button c-tk-icon-button--transparent-light';
		closeButtonEl.innerHTML = `
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20">
			  <path fill="currentColor" d="M3.85 2.32a1.09 1.09 0 1 0-1.53 1.53L8.46 10l-6.14 6.15a1.09 1.09 0 1 0 1.53 1.53L10 11.54l6.15 6.14a1.09 1.09 0 1 0 1.53-1.53L11.54 10l6.14-6.15a1.09 1.09 0 1 0-1.53-1.53L10 8.46 3.85 2.32Z"/>
			</svg>
		`;

		closeButtonEl.addEventListener('click', () => {
			this.closeGallery(galleryId);
		});

		actionsEl.appendChild(closeButtonEl);
		header.appendChild(actionsEl);

		// Content
		const contentEl = document.createElement('div');
		contentEl.className = 'c-tk-gallery__content';
		dialogEl.appendChild(contentEl);

		// Main Slider
		const mainSliderEl = document.createElement('div');
		mainSliderEl.className = 'c-tk-gallery__main';
		contentEl.appendChild(mainSliderEl);

		const slideTrackEl = document.createElement('div');
		slideTrackEl.className = 'glide__track';
		slideTrackEl.setAttribute('data-glide-el', 'track');
		mainSliderEl.appendChild(slideTrackEl);

		const slideListEl = document.createElement('ul');
		slideListEl.className = 'glide__slides';
		slideTrackEl.appendChild(slideListEl);

		[...slidesList].forEach((slide) => {
			const slideEl = document.createElement('li');
			slideEl.className = 'c-tk-gallery__slide glide__slide';

			const slideOverlayEl = document.createElement('div');
			slideOverlayEl.className = 'c-tk-gallery__slide-overlay';
			slideOverlayEl.addEventListener('click', () => {
				this.closeGallery(galleryId);
			});

			slideEl.appendChild(slideOverlayEl);
			slideEl.appendChild(slide.querySelector('[data-tk-gallery-slide]'));
			slideListEl.appendChild(slideEl);
		});

		const arrowsEl = document.createElement('div');
		arrowsEl.className = 'glide__arrows';
		arrowsEl.setAttribute('data-glide-el', 'controls');
		mainSliderEl.appendChild(arrowsEl);

		const arrowLeftEl = document.createElement('button');
		arrowLeftEl.className = 'glide__arrow glide__arrow--left';
		arrowLeftEl.setAttribute('data-glide-dir', '<');
		arrowLeftEl.innerHTML = `
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 9" width="20" height="20">
			  <path fill="currentColor" d="M4.885 9a1.116 1.116 0 0 0 .808-1.884L3.198 4.492l2.495-2.624A1.115 1.115 0 1 0 4.078.344L.85 3.718c-.411.43-.412 1.108-.003 1.539l3.172 3.434c.22.23.514.347.809.347z"/>
			</svg>
		`;
		arrowsEl.appendChild(arrowLeftEl);

		const arrowRightEl = document.createElement('button');
		arrowRightEl.className =
			'c-tk-icon-button c-tk-icon-button--transparent-light glide__arrow glide__arrow--right';
		arrowRightEl.setAttribute('data-glide-dir', '>');
		arrowRightEl.innerHTML = `
			<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 6 9" width="20" height="20">
			  <path fill="currentColor" d="M1.115 9a1.116 1.116 0 0 1-.808-1.884l2.495-2.624L.31 1.886A1.115 1.115 0 1 1 1.922.344L5.15 3.718c.411.43.412 1.108.003 1.539L1.925 8.652c-.22.23-.514.347-.809.347z"/>
			</svg>
		`;
		arrowsEl.appendChild(arrowRightEl);

		// Caption
		const captionEl = document.createElement('span');
		captionEl.className = 'c-tk-gallery__caption';
		contentEl.appendChild(captionEl);

		// Thumbnails Slider
		let thumbnailsSliderEl;

		if (hasThumbnails) {
			const thumbnailsWrapperEl = document.createElement('footer');
			thumbnailsWrapperEl.className = 'c-tk-gallery__thumbnails-wrapper';
			dialogEl.appendChild(thumbnailsWrapperEl);

			thumbnailsSliderEl = document.createElement('div');
			thumbnailsSliderEl.className = 'c-tk-gallery__thumbnails';
			thumbnailsWrapperEl.appendChild(thumbnailsSliderEl);

			const thumbnailsSliderTrackEl = document.createElement('div');
			thumbnailsSliderTrackEl.className = 'glide__track';
			thumbnailsSliderTrackEl.setAttribute('data-glide-el', 'track');
			thumbnailsSliderEl.appendChild(thumbnailsSliderTrackEl);

			const thumbnailsSliderListEl = document.createElement('ul');
			thumbnailsSliderListEl.className = 'glide__slides';
			thumbnailsSliderTrackEl.appendChild(thumbnailsSliderListEl);

			[...slidesList].forEach((slide, index) => {
				const thumbnailEl = document.createElement('li');
				thumbnailsSliderListEl.appendChild(thumbnailEl);

				thumbnailEl.className = 'c-tk-gallery__thumbnail glide__slide';
				thumbnailEl.appendChild(slide.querySelector('[data-tk-gallery-thumbnail]'));
				thumbnailEl.addEventListener('click', () => {
					this.navigateGallery(galleryId, index);
				});
			});
		}

		element.remove();

		return {
			id: galleryId,
			dialogEl,
			mainSliderEl,
			counterEl,
			captionEl,
			mainSlider: null,
			thumbnailsSliderEl,
			thumbnailsSlider: null,
		};
	}

	/**
	 * Creates the sliders for the gallery
	 * @param {Gallery} gallery
	 * @param {number} slideIndex
	 * @private
	 */
	_createGallerySliders(gallery, slideIndex = 0) {
		if (gallery.mainSlider) {
			return;
		}

		// Create Sliders
		gallery.mainSlider = new Glide(gallery.mainSliderEl, {
			type: 'carousel',
			perView: 1,
			gap: 0,
			startAt: slideIndex,
		});

		if (gallery.thumbnailsSliderEl) {
			gallery.thumbnailsSlider = new Glide(gallery.thumbnailsSliderEl, {
				type: 'slider',
				gap: 6,
				rewind: true,
				startAt: slideIndex,
				swipeThreshold: false,
				dragThreshold: false,
				peek: {
					before: 0,
					after: 36,
				},
			});
		}

		// Mount Main Slider
		gallery.mainSlider.mount({
			function(Glide, Components, Events) {
				function updateCounter() {
					const contentEl = Components.Html.slides[Glide.index]?.querySelector('[data-tk-gallery-slide]');

					Gallery.instance._updateGallery(gallery, {
						current: Glide.index + 1,
						total: Components.Sizes.length,
						caption: contentEl?.getAttribute('aria-label'),
					});
				}

				function updateThumbnails() {
					gallery.thumbnailsSlider?.go(`=${Glide.index}`);
				}

				Events.on('run', () => {
					updateCounter();
					updateThumbnails();
				});

				return {
					mount() {
						updateCounter();
					},
				};
			},
		});

		// Mount Thumbnails Slider
		gallery.thumbnailsSlider?.mount({
			function(Glide, Components, Events) {
				function updateSize() {
					const sliderElement = gallery.thumbnailsSliderEl;
					const slideWidth = 1.5 * sliderElement.clientHeight + 6;

					const perViewMin = Math.floor(sliderElement.parentElement.clientWidth / slideWidth);
					const perView = Math.min(Components.Sizes.length, perViewMin);

					const pageWidth = perView * slideWidth;
					const sliderWidth = Math.min(pageWidth, sliderElement.parentElement.clientWidth);

					sliderElement.style.width = `${sliderWidth}px`;

					Glide.update({
						perView,
					});
				}

				function updateFocus() {
					Glide.update({
						focusAt: Glide.index % Glide.settings.perView,
					});
				}

				Events.on('resize', () => {
					updateSize();
					updateFocus();
				});

				Events.on('run', () => {
					updateFocus();
				});

				return {
					mount() {
						updateSize();
						updateFocus();
					},
				};
			},
		});
	}

	/**
	 * @param {Gallery} gallery
	 * @param {{
	 *     current: number,
	 *     total: number,
	 *     caption: string
	 * }} config
	 * @private
	 */
	_updateGallery(gallery, config) {
		gallery.counterEl.innerHTML = `${config.current} / ${config.total}`;
		gallery.captionEl.innerHTML = config.caption;
	}
}

window.tkGallery = new Gallery();
